import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import {
  Images,
  Upload,
  History,
  Trash2,
  RotateCcw,
  X,
  Link as LinkIcon,
  Film,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { useAccount } from '../../context/AccountContext';
import {
  GET_MEDIA_ASSETS,
  GET_MEDIA_RENDITIONS,
  RESTORE_MEDIA_RENDITION,
  DELETE_MEDIA_ASSET,
} from '../../graphql';
import { sha256Hex, uploadAsset } from '../../lib/media/uploadAsset';
import {
  formatDuration,
  isVideoFile,
  uploadVideo,
  waitForVideo,
  type VideoInfo,
} from '../../lib/media/uploadVideo';
import { MediaAllowanceBar, useMediaAllowance } from '../media/MediaAllowance';

/**
 * The media library — the browser surface for the platform's asset layer.
 * Not yet extended: the Ink review only places Media next to Articles. This
 * grid, dedupe, and version history stay as they are. See src/styles/INK.md.
 *
 * Two things here are not the obvious implementation, and both are deliberate:
 *
 * 1. The browser hashes each file BEFORE uploading and sends only the digest.
 *    If the account already holds those bytes the server answers `deduped` and
 *    returns no upload URL, so re-adding a scan you already have costs one
 *    small request instead of several megabytes.
 *
 * 2. Bytes go straight to blob storage via a short-lived, write-only URL. They
 *    never pass through the API server, which is what keeps a burst of large
 *    originals from turning the API into a bottleneck.
 */

const PAGE_SIZE = 48;

interface Rendition {
  id: string;
  kind: string;
  url: string | null;
  width: number | null;
  height: number | null;
  bytes?: number | null;
  isCurrent: boolean;
  supersededAt?: string | null;
  createdAt: string;
  transform?: Record<string, unknown> | null;
}

interface MediaUsage {
  ownerType: string;
  ownerId: string;
  role: string;
  position: number;
  title: string | null;
  status: string | null;
}

interface MediaAsset {
  id: string;
  sha256: string;
  mime: string | null;
  bytes: number | null;
  width: number | null;
  height: number | null;
  originalFilename: string | null;
  captureMeta?: Record<string, unknown> | null;
  createdAt: string;
  renditions: Rendition[];
  usage: MediaUsage[];
  usageCount: number;
  video?: VideoInfo | null;
}

const VIDEO_STATUS: Record<string, string> = {
  uploading: 'uploading',
  processing: 'processing',
  error: 'failed',
  rejected: 'too long for your plan',
};

/** A video tile: its poster with the length, or where it is in processing. */
function VideoThumb({ asset, className }: { asset: MediaAsset; className?: string }) {
  const v = asset.video!;
  return (
    <>
      {v.posterUrl ? (
        <img
          src={v.posterUrl}
          alt={asset.originalFilename || 'video'}
          loading="lazy"
          className={className}
        />
      ) : (
        <Film className="w-6 h-6 opacity-40" />
      )}
      <span
        style={{
          position: 'absolute',
          left: 6,
          bottom: 6,
          padding: '1px 6px',
          borderRadius: 6,
          fontSize: 11,
          background: 'rgba(0,0,0,0.65)',
          color: '#fff',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <Film className="w-3 h-3" />
        {v.status === 'ready'
          ? formatDuration(v.durationSeconds)
          : VIDEO_STATUS[v.status] || v.status}
      </span>
    </>
  );
}

/** Prefer the current canonical rendition; fall back to the original. */
function displayRendition(asset: MediaAsset): Rendition | null {
  const current = asset.renditions?.filter((r) => r.isCurrent) ?? [];
  return (
    current.find((r) => r.kind === 'thumb') ??
    current.find((r) => r.kind === 'web') ??
    current.find((r) => r.kind === 'canonical') ??
    current.find((r) => r.kind === 'original') ??
    null
  );
}

/** One-line "where is this used" summary, for the badge tooltip. */
function usageSummary(usage: MediaUsage[]): string {
  if (!usage?.length) return 'Not used anywhere';
  return usage
    .map((u) => `${u.title || u.ownerType} (${u.role}${u.status ? `, ${u.status}` : ''})`)
    .join('\n');
}

function formatBytes(n: number | null | undefined): string {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

type UploadState = {
  name: string;
  status: 'hashing' | 'uploading' | 'done' | 'deduped' | 'error';
  message?: string;
};

export function MediaLibrary({
  embedded = false,
  onReady,
}: {
  embedded?: boolean;
  /** Opens the file picker. The Content header uses this for its Upload button. */
  onReady?: (openUpload: () => void) => void;
}) {
  const { accountId, isLoading: accountsLoading } = useAccount();
  const client = useApolloClient();
  const { allowance, refetch: refetchAllowance } = useMediaAllowance(accountId);
  const [offset, setOffset] = useState(0);
  const [fileQuery, setFileQuery] = useState('');
  const [usage, setUsage] = useState<'all' | 'used' | 'unused'>('all');
  const [uploads, setUploads] = useState<UploadState[]>([]);
  const [historyFor, setHistoryFor] = useState<MediaAsset | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useEffect(() => {
    onReadyRef.current?.(() => fileInput.current?.click());
  }, []);

  const { data, loading, error, refetch } = useQuery(GET_MEDIA_ASSETS, {
    variables: { accountId, limit: PAGE_SIZE, offset },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });

  const [deleteAsset] = useMutation(DELETE_MEDIA_ASSET);

  const page = data?.mediaAssets;
  const assets: MediaAsset[] = useMemo(() => page?.assets ?? [], [page]);
  const shown = useMemo(() => {
    const q = fileQuery.trim().toLowerCase();
    return assets.filter((asset) => {
      if (usage === 'used' && !(asset.usageCount > 0)) return false;
      if (usage === 'unused' && asset.usageCount > 0) return false;
      if (q && !(asset.originalFilename || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [assets, fileQuery, usage]);
  const usedCount = assets.filter((asset) => asset.usageCount > 0).length;
  const unusedCount = assets.filter((asset) => !(asset.usageCount > 0)).length;

  const setUpload = (name: string, patch: Partial<UploadState>) =>
    setUploads((prev) => prev.map((u) => (u.name === name ? { ...u, ...patch } : u)));

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files?.length || !accountId) return;
      const list = Array.from(files);
      setUploads(list.map((f) => ({ name: f.name, status: 'hashing' as const })));

      for (const file of list) {
        if (isVideoFile(file)) {
          // Straight to video hosting, in resumable pieces; encoded after.
          try {
            setUpload(file.name, { status: 'uploading', message: '0%' });
            const { assetId } = await uploadVideo(client, accountId, file, (p) =>
              setUpload(file.name, { message: `${p}%` })
            );
            setUpload(file.name, {
              status: 'done',
              message: 'processing — ready in a few minutes',
            });
            void refetch();
            void waitForVideo(client, accountId, assetId)
              .then(() => setUpload(file.name, { status: 'done', message: 'ready' }))
              .catch((err) =>
                setUpload(file.name, { status: 'error', message: (err as Error).message })
              )
              .finally(() => {
                void refetch();
                void refetchAllowance();
              });
          } catch (err) {
            setUpload(file.name, {
              status: 'error',
              message: err instanceof Error ? err.message : 'Upload failed',
            });
          }
          continue;
        }
        try {
          const sha256 = await sha256Hex(file);
          setUpload(file.name, { status: 'uploading' });

          const result = await uploadAsset(accountId, file, {
            sha256,
            filename: file.name,
          });

          setUpload(
            file.name,
            result.deduped
              ? { status: 'deduped', message: 'already in your library — nothing uploaded' }
              : { status: 'done' }
          );
        } catch (err) {
          setUpload(file.name, {
            status: 'error',
            message: err instanceof Error ? err.message : 'Upload failed',
          });
        }
      }
      await refetch();
      void refetchAllowance();
    },
    [accountId, client, refetch, refetchAllowance]
  );

  const handleDelete = async (asset: MediaAsset) => {
    if (!accountId) return;
    // Soft delete server-side: the bytes are retained, so this is recoverable
    // by re-uploading the same file rather than being destructive.
    // Deleting also detaches everywhere, so say what would break rather than
    // letting an image vanish from a published item without warning.
    const label = asset.originalFilename || 'this image';
    const warning =
      asset.usageCount > 0
        ? `${label} is used by ${asset.usageCount} item${asset.usageCount > 1 ? 's' : ''}:\n\n` +
          `${usageSummary(asset.usage)}\n\nRemoving it will detach it from ${
            asset.usageCount > 1 ? 'them' : 'it'
          }. Continue?`
        : `Remove ${label} from the library?`;
    if (!window.confirm(warning)) {
      return;
    }
    try {
      await deleteAsset({ variables: { accountId, assetId: asset.id } });
    } catch (err) {
      // e.g. a video still on a page: the server says which page.
      window.alert(err instanceof Error ? err.message : 'Couldn’t remove it');
      return;
    }
    await refetch();
    void refetchAllowance();
  };

  if (accountsLoading) return <div className="p-6 text-cmp-ink-3">Loading…</div>;

  const library = (
    <div
      className={
        embedded
          ? 'flex flex-col flex-1 min-h-0 overflow-hidden'
          : 'flex-1 min-h-0 overflow-y-auto space-y-6 pr-1'
      }
    >
      {embedded ? (
        <div className="ink-lib-filters" style={{ flex: 'none' }}>
          <input
            className="ink-search ink-search-files"
            value={fileQuery}
            onChange={(e) => setFileQuery(e.target.value)}
            placeholder="Search file names"
          />
          {(
            [
              ['all', 'All', page?.total ?? assets.length],
              ['used', 'In use', usedCount],
              ['unused', 'Unused', unusedCount],
            ] as const
          ).map(([id, label, count]) => (
            <button
              key={id}
              type="button"
              className="ink-chip"
              data-active={usage === id ? 'true' : 'false'}
              onClick={() => setUsage(id)}
            >
              {label} <span>{count}</span>
            </button>
          ))}
          <input
            ref={fileInput}
            type="file"
            multiple
            accept="image/*,video/*"
            className="hidden"
            onChange={(e) => {
              void handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </div>
      ) : (
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold text-cmp-ink flex items-center gap-2">
              <Images className="w-6 h-6" /> Media
            </h1>
            <p className="text-sm text-cmp-ink-2 mt-1">
              Every original is kept. Crops and resizes are new versions, so an edit never destroys
              the scan underneath it.
            </p>
          </div>
          <div>
            <input
              ref={fileInput}
              type="file"
              multiple
              accept="image/*,video/*"
              className="hidden"
              onChange={(e) => {
                void handleFiles(e.target.files);
                e.target.value = '';
              }}
            />
            <Button onClick={() => fileInput.current?.click()} className="gap-2">
              <Upload className="w-4 h-4" /> Upload images or videos
            </Button>
          </div>
        </header>
      )}

      <div className={embedded ? 'ink-scroll flex-1 min-h-0 overflow-y-auto' : 'contents'}>
        <MediaAllowanceBar allowance={allowance} />
        {uploads.length > 0 && (
          <Card className="p-4 space-y-2 bg-cmp-raise border border-cmp-line text-cmp-ink">
            {uploads.map((u) => (
              <div key={u.name} className="flex items-center gap-3 text-sm">
                <span className="text-cmp-ink truncate max-w-xs">{u.name}</span>
                {u.status === 'hashing' && <Badge variant="outline">hashing…</Badge>}
                {u.status === 'uploading' && <Badge variant="outline">uploading…</Badge>}
                {u.status === 'done' && <Badge>added</Badge>}
                {u.status === 'deduped' && <Badge variant="outline">already had it</Badge>}
                {u.status === 'error' && <Badge variant="destructive">failed</Badge>}
                {u.message && <span className="text-cmp-ink-3">{u.message}</span>}
              </div>
            ))}
            <button
              className="text-xs text-cmp-ink-2 hover:text-cmp-ink underline"
              onClick={() => setUploads([])}
            >
              clear
            </button>
          </Card>
        )}

        {error && (
          <Card className="p-4 text-sm text-red-400 bg-cmp-raise border border-cmp-line text-cmp-ink">
            Could not load media: {error.message}
          </Card>
        )}

        {!loading && assets.length === 0 && (
          <Card className="p-10 text-center text-cmp-ink-2 bg-cmp-raise border border-cmp-line text-cmp-ink">
            <Images className="w-[2rem] h-[2rem] mx-auto mb-3 opacity-50" />
            <p>No media yet. Upload an image or a video, or capture one from the studio app.</p>
          </Card>
        )}

        <div
          className={
            embedded ? 'ink-media-grid' : 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4'
          }
        >
          {shown.map((asset) => {
            const preview = displayRendition(asset);
            const versions = asset.renditions?.length ?? 0;
            const width = asset.width || preview?.width;
            const height = asset.height || preview?.height;
            const meta = [formatBytes(asset.bytes), width && height ? `${width}×${height}` : null]
              .filter(Boolean)
              .join(' · ');
            const used = asset.usageCount > 0;
            if (embedded) {
              return (
                <div key={asset.id} className="ink-media-card">
                  <div className="ink-media-thumb">
                    {asset.video ? (
                      <VideoThumb asset={asset} />
                    ) : preview?.url ? (
                      <img
                        src={preview.url}
                        alt={asset.originalFilename || 'media'}
                        loading="lazy"
                      />
                    ) : (
                      <span className="ink-media-fallback">card scan</span>
                    )}
                    <div className="ink-media-tools">
                      <button
                        type="button"
                        onClick={() => setHistoryFor(asset)}
                        title="Version history"
                      >
                        <History className="w-3 h-3" /> {versions}
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDelete(asset)}
                        title="Remove from library"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  <div className="ink-media-name" title={asset.originalFilename || ''}>
                    {asset.originalFilename || asset.sha256.slice(0, 10)}
                  </div>
                  <div className="ink-media-meta">
                    <span>{meta}</span>
                    <span
                      style={{ color: used ? 'var(--ink-ink3)' : 'var(--ink-amber)' }}
                      title={used ? usageSummary(asset.usage) : 'Nothing references this image.'}
                    >
                      {used ? `Used in ${asset.usageCount}` : 'Unused'}
                    </span>
                  </div>
                </div>
              );
            }
            return (
              <Card
                key={asset.id}
                className="overflow-hidden group bg-cmp-raise border border-cmp-line text-cmp-ink"
              >
                <div className="aspect-[3/4] bg-cmp-surface flex items-center justify-center overflow-hidden relative">
                  {asset.video ? (
                    <VideoThumb asset={asset} className="w-full h-full object-contain" />
                  ) : preview?.url ? (
                    <img
                      src={preview.url}
                      alt={asset.originalFilename || 'media'}
                      loading="lazy"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <Images className="w-6 h-6 opacity-40" />
                  )}
                </div>
                <div className="p-2 space-y-1">
                  <div
                    className="text-xs text-cmp-ink truncate"
                    title={asset.originalFilename || ''}
                  >
                    {asset.originalFilename || asset.sha256.slice(0, 10)}
                  </div>
                  <div className="text-[11px] text-cmp-ink-2 flex items-center gap-2">
                    <span>{formatBytes(asset.bytes)}</span>
                    {preview?.width && preview?.height && (
                      <span>
                        {preview.width}×{preview.height}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    {asset.usageCount > 0 ? (
                      <Badge
                        variant="outline"
                        className="text-[10px] gap-1"
                        title={usageSummary(asset.usage)}
                      >
                        <LinkIcon className="w-2.5 h-2.5" />
                        Used {asset.usageCount > 1 ? `${asset.usageCount}×` : ''}
                      </Badge>
                    ) : (
                      <Badge
                        variant="outline"
                        className="text-[10px] opacity-60"
                        title="Nothing references this image — safe to remove."
                      >
                        Unused
                      </Badge>
                    )}
                    {asset.usageCount === 1 && asset.usage[0]?.title && (
                      <span
                        className="text-[10px] text-cmp-ink-3 truncate max-w-[9rem]"
                        title={asset.usage[0].title}
                      >
                        {asset.usage[0].title}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 pt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 gap-1 text-[11px]"
                      onClick={() => setHistoryFor(asset)}
                      title="Version history"
                    >
                      <History className="w-3 h-3" /> {versions}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-[11px]"
                      onClick={() => void handleDelete(asset)}
                      title="Remove from library"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {embedded && shown.length > 0 && (!page || offset + PAGE_SIZE >= page.total) && (
          <div className="ink-list-end">
            <div className="ink-list-end-row">
              <span className="ink-list-end-rule" />
              <span className="ink-list-end-gem" />
              <span className="ink-list-end-rule" />
            </div>
            <p className="ink-list-end-label">The end.</p>
            <div className="ink-list-end-note">No more files</div>
          </div>
        )}

        {page && page.total > PAGE_SIZE && (
          <div className="flex items-center justify-between text-sm text-cmp-ink-2">
            <span>
              {offset + 1}–{Math.min(offset + PAGE_SIZE, page.total)} of {page.total}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={offset + PAGE_SIZE >= page.total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {historyFor && (
          <VersionHistory
            asset={historyFor}
            accountId={accountId!}
            onClose={() => setHistoryFor(null)}
            onRestored={() => void refetch()}
          />
        )}
      </div>
    </div>
  );

  if (embedded) return library;

  return (
    <div className="ink-page flex w-full flex-col h-full min-h-0 overflow-hidden">
      <div className="flex flex-col flex-1 min-h-0 overflow-y-auto md:overflow-hidden">
        <div className="w-full max-w-full overflow-hidden relative flex-1 flex flex-col">
          <div className="m-0 w-full h-full relative overflow-hidden box-border bg-primary-background flex flex-col">
            <div className="ink-frame">{library}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Version history for one asset.
 *
 * Restoring is a pointer swap, not a copy — every version's bytes are still in
 * storage — so reverting a bad crop is instant and cannot half-fail.
 */
function VersionHistory({
  asset,
  accountId,
  onClose,
  onRestored,
}: {
  asset: MediaAsset;
  accountId: string;
  onClose: () => void;
  onRestored: () => void;
}) {
  const { data, loading, refetch } = useQuery(GET_MEDIA_RENDITIONS, {
    variables: { accountId, assetId: asset.id },
    fetchPolicy: 'network-only',
  });
  const [restore, { loading: restoring }] = useMutation(RESTORE_MEDIA_RENDITION);
  const renditions: Rendition[] = data?.mediaRenditions ?? [];

  const handleRestore = async (renditionId: string) => {
    await restore({ variables: { accountId, renditionId } });
    await refetch();
    onRestored();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-6">
      <Card className="w-full max-w-3xl max-h-[80vh] overflow-auto p-5 space-y-4 bg-cmp-raise border border-cmp-line text-cmp-ink shadow-2xl">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-cmp-ink">
              {asset.originalFilename || 'Image'}
            </h2>
            <p className="text-xs text-cmp-ink-2 mt-1">
              Where it is used, and every version of it. The original is never replaced — each crop
              or resize is added alongside it.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="border border-cmp-line rounded p-3">
          <div className="text-xs font-medium text-cmp-ink mb-2">Used by</div>
          {asset.usage.length === 0 ? (
            <p className="text-xs text-cmp-ink-2">
              Nothing references this image. Removing it affects no content.
            </p>
          ) : (
            <ul className="space-y-1">
              {asset.usage.map((u) => (
                <li
                  key={`${u.ownerType}-${u.ownerId}-${u.role}-${u.position}`}
                  className="text-xs flex items-center gap-2"
                >
                  <Badge variant="outline" className="text-[10px]">
                    {u.role}
                  </Badge>
                  {u.ownerType === 'topic' ? (
                    <a
                      href={`/account/content/${u.ownerId}`}
                      className="text-cmp-ink underline underline-offset-2 truncate"
                    >
                      {u.title || u.ownerId}
                    </a>
                  ) : (
                    <span className="text-cmp-ink truncate">{u.title || u.ownerId}</span>
                  )}
                  <span className="text-cmp-ink-3">
                    {u.ownerType.replace('_', ' ')}
                    {u.status ? ` · ${u.status}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {loading && <div className="text-sm text-cmp-ink-2">Loading versions…</div>}

        <div className="space-y-2">
          {renditions.map((r) => (
            <div key={r.id} className="flex items-center gap-3 p-2 rounded border border-cmp-line">
              <div className="w-16 h-20 bg-cmp-surface flex items-center justify-center overflow-hidden rounded">
                {r.url ? (
                  <img src={r.url} alt={r.kind} className="w-full h-full object-contain" />
                ) : (
                  <Images className="w-4 h-4 opacity-40" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Badge variant={r.kind === 'original' ? 'default' : 'outline'}>{r.kind}</Badge>
                  {r.isCurrent && <Badge>current</Badge>}
                  {r.transform?.source ? (
                    <span className="text-[11px] text-cmp-ink-2">
                      via {String(r.transform.source)}
                    </span>
                  ) : null}
                </div>
                <div className="text-[11px] text-cmp-ink-2 mt-1">
                  {r.width && r.height ? `${r.width}×${r.height} · ` : ''}
                  {formatBytes(r.bytes)} · {new Date(r.createdAt).toLocaleString()}
                </div>
              </div>
              {!r.isCurrent && r.kind !== 'original' && (
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1"
                  disabled={restoring}
                  onClick={() => void handleRestore(r.id)}
                >
                  <RotateCcw className="w-3 h-3" /> Restore
                </Button>
              )}
            </div>
          ))}
          {!loading && renditions.length === 0 && (
            <div className="text-sm text-cmp-ink-2">No versions recorded yet.</div>
          )}
        </div>
      </Card>
    </div>
  );
}
